import { isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  inject,
  Injectable,
  NgZone,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { fromEvent, interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import SessionStorageService, {
  SessionStorageKey,
} from '@shared/services/session-storage.service';
import {
  APP_UPDATE_MAX_FETCH_FAILURES,
  APP_UPDATE_MIN_CHECK_GAP_MS,
  APP_UPDATE_NOTIFICATION_CLASS,
  APP_UPDATE_POLL_INTERVAL_MS,
  APP_UPDATE_RELOAD_COOLDOWN_MS,
  APP_UPDATE_SILENT_RELOAD_AWAY_MS,
  APP_VERSION_FILE_PATH,
  STALE_BUILD_ERROR_PATTERN,
} from './app-update.constants';
import { AppUpdateToastTemplate, BuildVersion } from './app-update.types';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly notification = inject(NzNotificationService);
  private readonly modal = inject(NzModalService);
  private readonly sessionStorage = inject(SessionStorageService);

  public readonly updateAvailable = signal(false);

  private toastTemplate: AppUpdateToastTemplate | null = null;
  private loadedBuildId: string | null = null;
  private lastCheckedAt = 0;
  private fetchFailures = 0;
  private hiddenSince: number | null = null;

  public start(toastTemplate: AppUpdateToastTemplate): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.toastTemplate = toastTemplate;
    this.watchStaleBuildFailures();
    this.watchCheckTriggers();
    void this.check();
  }

  public reload(): void {
    this.markReload();
    window.location.reload();
  }

  private watchCheckTriggers(): void {
    this.ngZone.runOutsideAngular(() =>
      interval(APP_UPDATE_POLL_INTERVAL_MS)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.ngZone.run(() => void this.check())),
    );

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => void this.check());

    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onVisibilityChange());
  }

  private onVisibilityChange(): void {
    if (document.visibilityState !== 'visible') {
      this.hiddenSince = Date.now();
      return;
    }

    const awayMs = this.hiddenSince ? Date.now() - this.hiddenSince : 0;
    this.hiddenSince = null;

    void this.check(this.canReloadSilently(awayMs));
  }

  private canReloadSilently(awayMs: number): boolean {
    return (
      awayMs >= APP_UPDATE_SILENT_RELOAD_AWAY_MS &&
      !this.modal.openModals.length &&
      !this.reloadedRecently()
    );
  }

  private async check(reloadSilently = false): Promise<void> {
    if (this.updateAvailable() || document.visibilityState !== 'visible') {
      return;
    }

    if (this.fetchFailures >= APP_UPDATE_MAX_FETCH_FAILURES) {
      return;
    }

    const now = Date.now();
    if (now - this.lastCheckedAt < APP_UPDATE_MIN_CHECK_GAP_MS) {
      return;
    }
    this.lastCheckedAt = now;

    const buildId = await this.fetchBuildId(now);
    if (!buildId) {
      return;
    }

    if (!this.loadedBuildId) {
      this.loadedBuildId = buildId;
      return;
    }

    if (buildId !== this.loadedBuildId) {
      this.updateAvailable.set(true);

      if (reloadSilently) {
        this.reload();
        return;
      }

      this.showToast();
    }
  }

  private async fetchBuildId(stamp: number): Promise<string | null> {
    try {
      const response = await fetch(`${APP_VERSION_FILE_PATH}?_=${stamp}`, {
        cache: 'no-store',
      });
      if (!response.ok) {
        this.fetchFailures += 1;
        return null;
      }

      const payload = (await response.json()) as Partial<BuildVersion>;
      this.fetchFailures = 0;
      return payload?.buildId ?? null;
    } catch {
      this.fetchFailures += 1;
      return null;
    }
  }

  private showToast(): void {
    if (!this.toastTemplate) {
      return;
    }

    this.notification.template(this.toastTemplate, {
      nzDuration: 0,
      nzClass: APP_UPDATE_NOTIFICATION_CLASS,
    });
  }

  private watchStaleBuildFailures(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationError => event instanceof NavigationError,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.recoverFromStaleBuild(event.error, event.url));

    fromEvent<PromiseRejectionEvent>(window, 'unhandledrejection')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.recoverFromStaleBuild(event.reason, null));
  }

  private recoverFromStaleBuild(error: unknown, url: string | null): void {
    if (!this.isStaleBuildError(error) || this.reloadedRecently()) {
      return;
    }

    this.markReload();

    if (url) {
      window.location.assign(url);
      return;
    }

    window.location.reload();
  }

  private isStaleBuildError(error: unknown): boolean {
    const message =
      error instanceof Error
        ? `${error.name} ${error.message}`
        : String(error ?? '');

    return STALE_BUILD_ERROR_PATTERN.test(message);
  }

  private reloadedRecently(): boolean {
    const raw = this.sessionStorage.getItem(
      SessionStorageKey.APP_UPDATE_RELOADED_AT,
    );
    const reloadedAt = Number(raw);

    return (
      Number.isFinite(reloadedAt) &&
      reloadedAt > 0 &&
      Date.now() - reloadedAt < APP_UPDATE_RELOAD_COOLDOWN_MS
    );
  }

  private markReload(): void {
    this.sessionStorage.setItem(
      SessionStorageKey.APP_UPDATE_RELOADED_AT,
      String(Date.now()),
    );
  }
}
